import { collection } from 'firebase/firestore';
import { useFirestore, useFirestoreCollection } from 'reactfire';

// 2026-09-01: for the Reports dashboard. Reads the whole `titles` collection client-
// side and reduces each doc to just the fields the dashboard's aggregations need --
// same "read everything, compute in the browser" approach useAllTitlesLite already
// uses for the duplicate-title check (Add Title), at the same collection size (5,000+
// docs today). No Cloud Functions triggers exist anywhere in this codebase to
// incrementally maintain a smaller aggregate doc, and this collection is already
// read in full elsewhere without issue, so this doesn't introduce a new pattern or
// new backend infra -- see the Reports dashboard PR description for the fuller
// reasoning on why a maintained aggregate doc isn't warranted yet at this scale.
//
// Firestore has no field-projection on get()/onSnapshot() -- a query always returns
// whole documents regardless of which fields you read afterward -- so reducing to
// fewer fields here only saves memory/readability in this app, not network transfer.
export function useTitlesStats() {
    const firestore = useFirestore();
    const titlesCollection = collection(firestore, 'titles');
    const { status, data: titleDocs } = useFirestoreCollection(titlesCollection);

    if (status === 'loading') {
        return undefined;
    }

    const titles = [];
    titleDocs.forEach(doc => {
        const data = doc.data();
        titles.push({
            id: doc.id,
            title: data.title,
            // channels: array of 1 (BlackPhlix) and/or 2 (FindFlix). Every title has
            // this (confirmed via a direct Firestore query, 2026-09-01) -- no
            // "missing brand" fallback needed the way other fields below have one.
            channels: Array.isArray(data.channels) ? data.channels : [],
            // kind: 1 = movie, 2 = series. Defaults to movie if somehow absent,
            // matching TitleDetailPage.js's existing "undefined kind reads as..."
            // convention elsewhere in this codebase -- though in practice every
            // title has this field too.
            kind: (data.kind === 2 || data.kind === '2') ? 2 : 1,
            creator: data.creator || undefined,
            // genres: plain strings (NOT guids) -- multi-valued, not a partition.
            // Deliberately NOT cross-checked against the `genres` collection here:
            // the orphaned-genre-string cleanup is an explicitly separate, deferred
            // piece of work (see PR description) -- this dashboard just tallies
            // whatever strings actually appear on titles, as-is.
            genres: Array.isArray(data.genres) ? data.genres : [],
            // tags: guids referencing the `tags` collection (NOT plain strings,
            // unlike genres -- confirmed via TagChooser.js's checkbox values) --
            // resolved to display names by the Reports page via useTags().
            tags: Array.isArray(data.tags) ? data.tags : [],
            language: data.language || undefined,
            hasDirector: !!(data.directors && Object.keys(data.directors).length > 0),
            hasRating: !!data.rating,
            hasSynopsis: !!data.synopsis,
            hasPoster: !!data.thumbnail,
        });
    });

    return titles;
}
